import { omit } from 'lodash';

import { ia } from 'lib/helpers/utility';
import { useIsShellHosted } from 'lib/context/ShellContext/ShellContext';

import Button from 'components/shared/Button/Button';

import Popover from '../Popovers/Popover/Popover';

import AppliedFilters from './components/AppliedFilters';
import { hasActiveFilters } from './lib/hasActiveFilters';
import { useFilterOptions } from './hooks/useFilterOptions';
import SuggestedFilters from './components/SuggestedFilters';

const SHELL_SUGGESTED_COUNT = 3;

const Filters = ({
  defaultFilters,
  filters,
  changeFilters,
  updateFilters,
  appliedFilters = true,
  position,
  hideOperator = false,
  valueRenderers = {},
  clearable = false,
  onClearFilters,
  // A count, or `true` for the shell's count inside the shell and none outside it.
  suggested: suggestedProp = 0,
  // The table's own quick filters, in order: exactly these become chips (an empty list: none).
  quickKeys,
  // Filters never offered as chips, though still in the menu (a status the view tabs own).
  notQuick = [],
  // Filters the table pins another way (a status view pins the status): never offered or shown.
  hiddenKeys = [],
  ...rest
}) => {
  const isHosted = useIsShellHosted();
  const suggested = suggestedProp === true ? (isHosted ? SHELL_SUGGESTED_COUNT : 0) : suggestedProp;
  const offeredFilters = omit(defaultFilters, hiddenKeys);
  const shownFilters = omit(filters, hiddenKeys);

  const filterOptions = useFilterOptions({ defaultFilters: offeredFilters, changeFilters });
  const appliedKeys = suggested > 0 ? Object.keys(shownFilters || {}) : [];
  const menuOptions = filterOptions.filter((option) => !appliedKeys.includes(option.key));

  const renderMenu = (shownKeys = [], hasSuggestions = false) => {
    const options = menuOptions.filter((option) => !shownKeys.includes(option.key));
    if (!ia(options)) return null;

    return (
      <div className={suggested > 0 ? 'relative shrink-0' : 'relative flex-1'}>
        <Popover
          customButtonTrigger={({ open }) => (
            <Button
              variant="tertiary"
              text={hasSuggestions || ia(appliedKeys) ? 'More filters' : 'Filter'}
              textClassName="select-none whitespace-nowrap"
              icon="sort-mono"
              size="xs"
              color="neutral"
              className={open && 'bg-neutral-100'}
            />
          )}
          optionArrowIcon="chevron-right-filled"
          optionIconColor="neutral"
          iconsShade={700}
          options={options}
          textClassName="text-neutral-900"
          isFixed
          position={position}
          {...rest}
        />
      </div>
    );
  };

  return (
    <div className="flex flex-1 flex-wrap items-start gap-2">
      {appliedFilters && (
        <AppliedFilters
          filters={shownFilters}
          changeFilters={changeFilters}
          updateFilters={updateFilters}
          defaultFilters={defaultFilters}
          position={position}
          hideOperator={hideOperator}
          valueRenderers={valueRenderers}
          clearable={clearable}
        />
      )}

      {onClearFilters && hasActiveFilters(shownFilters, { withValues: true }) && (
        <Button
          variant="link"
          color="danger"
          size="xs"
          text="Clear filters"
          textClassName="select-none whitespace-nowrap"
          onClick={onClearFilters}
        />
      )}

      {suggested > 0 ? (
        <SuggestedFilters
          defaultFilters={offeredFilters}
          filters={filters}
          changeFilters={changeFilters}
          updateFilters={updateFilters}
          limit={suggested}
          quickKeys={quickKeys}
          exclude={notQuick}
          position={position}
          menuKeys={menuOptions.map((option) => option.key)}
          renderMenu={renderMenu}
        />
      ) : (
        renderMenu()
      )}
    </div>
  );
};

export default Filters;
